import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Button,
  Grid,
  Pagination,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { apiClient } from '@/services/api';

interface SubscriptionRecord {
  id: number;
  orderId: string;
  status: string;
  createdAt: string;
  startsAt: string;
  endsAt: string;
  autoRenew: boolean;
  projectCreditsTotal: number;
  projectCreditsUsed: number;
  maxUsers: number;
  currentUsers: number;
  user: {
    id: number;
    username: string;
    displayName: string;
    email: string;
    role: string;
    organizationName: string;
  };
  plan: {
    name: string;
    price: number;
    durationValue: number;
    durationUnit: string;
  };
  payment: {
    method: string;
    status: string;
    paidAt: string;
  };
}

interface PaymentRecord {
  id: number;
  order_id: string;
  user_id: number;
  org_id: number;
  plan_id: number;
  amount_cents: number;
  amount_yuan: number;
  payment_method: string;
  payment_status: string;
  project_credits_total: number;
  max_users: number;
  subscription_id: number;
  created_at: string;
  updated_at: string;
  plan_name?: string;
  organization_name?: string;
  user_name?: string;
}

interface User {
  id: number;
  username: string;
  email: string;
  display_name: string;
  role: string;
  is_active: boolean;
  org_id: number;
  created_at: string;
  updated_at: string;
}

export default function SubscriptionRecordsAdmin() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab] = useState<'subscriptions' | 'payments'>('subscriptions');

  const [subscriptions, setSubscriptions] = useState<SubscriptionRecord[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const [filters, setFilters] = useState({
    search: '',
    status: ''
  });

  const [payments, setPayments] = useState<PaymentRecord[]>([]);

  const [detailDialog, setDetailDialog] = useState({
    open: false,
    record: null as SubscriptionRecord | PaymentRecord | null
  });

  useEffect(() => {
    loadCurrentUser();
  }, []);

  useEffect(() => {
    if (user && activeTab === 'subscriptions') {
      loadSubscriptions();
    }
  }, [user, activeTab, pagination.page, filters]);

  useEffect(() => {
    if (user && activeTab === 'payments') {
      loadPayments();
    }
  }, [user, activeTab]);

  const loadCurrentUser = async () => {
    try {
      const response = await apiClient.users.getMe();
      if (response.success) {
        setUser(response.data);
      }
    } catch (err) {
      setError('获取用户信息失败');
      console.error('Failed to load current user:', err);
    }
  };

  const loadSubscriptions = async () => {
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.limit.toString(),
        ...filters
      });

      const response = await apiClient.admin.getSubscriptionRecords(Object.fromEntries(params));
      if (response.success) {
        setSubscriptions(response.data.subscriptions || []);
        setPagination(response.data.pagination || pagination);
      } else {
        setError(response.message || '加载订阅记录失败');
      }
    } catch (err) {
      setError('网络错误，请稍后重试');
      console.error('加载订阅记录失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      const response = await apiClient.admin.getPaymentRecords({
        page: pagination.page,
        page_size: pagination.limit
      });
      if (response.success) {
        setPayments(response.data.records || []);
        setPagination({
          page: response.data.page || 1,
          limit: response.data.page_size || 20,
          total: response.data.total || 0,
          totalPages: response.data.total_pages || 0
        });
      } else {
        setError(response.message || '加载支付记录失败');
      }
    } catch (err) {
      setError('网络错误，请稍后重试');
      console.error('加载支付记录失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    if (activeTab === 'subscriptions') {
      loadSubscriptions();
    } else {
      loadPayments();
    }
  };

  const handleViewDetails = (record: SubscriptionRecord | PaymentRecord) => {
    setDetailDialog({ open: true, record });
  };

  const handleCloseDetails = () => {
    setDetailDialog({ open: false, record: null });
  };

  const getStatusChip = (status: string) => {
    const statusMap: Record<string, { label: string; color: any }> = {
      active: { label: '生效中', color: 'success' },
      expired: { label: '已过期', color: 'error' },
      cancelled: { label: '已取消', color: 'default' },
      pending: { label: '待支付', color: 'warning' }
    };
    const config = statusMap[status] || { label: status, color: 'default' };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const formatPaymentMethod = (method: string) => {
    const methodMap: Record<string, string> = {
      wechat_pay: '微信支付',
      bank_transfer: '对公转账'
    };
    return methodMap[method] || method;
  };

  const formatUserRole = (role: string) => {
    const roleMap: Record<string, string> = {
      super_admin: '超级管理员',
      admin: '管理员',
      writer: '编写员',
      zhaoyuan_admin: '昭元管理员'
    };
    return roleMap[role] || role;
  };

  const formatPrice = (price: number) => `¥${(price / 100).toFixed(2)}`;

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('zh-CN');
  };

  const formatPaymentDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const chinaTime = new Date(date.getTime() + 8 * 60 * 60 * 1000);
    return chinaTime.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (!['super_admin', 'zhaoyuan_admin'].includes(user.role || '')) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">权限不足：只有超级管理员和昭元管理员可以访问此内容</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="搜索用户名称"
                value={filters.search}
                onChange={(e) => handleSearchChange(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={handleSearch} edge="end">
                      <SearchIcon />
                    </IconButton>
                  )
                }}
              />
            </Grid>
            {/* 仅保留用户名称搜索，移除状态筛选/重置/刷新 */}
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {activeTab === 'subscriptions' ? (
        <Card>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>订单号</TableCell>
                  <TableCell>用户信息</TableCell>
                  <TableCell>公司</TableCell>
                  <TableCell>套餐</TableCell>
                  <TableCell>金额</TableCell>
                  <TableCell>支付方式</TableCell>
                  <TableCell>项目个数</TableCell>
                  <TableCell>已使用个数</TableCell>
                  <TableCell>最大子账号数</TableCell>
                  <TableCell>当前子账号数</TableCell>
                  <TableCell>状态</TableCell>
                  <TableCell>支付时间</TableCell>
                  <TableCell>到期时间</TableCell>
                  <TableCell>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={14} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : subscriptions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">暂无订阅记录</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  subscriptions.map((subscription) => (
                    <TableRow key={subscription.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">{subscription.orderId}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {subscription.user.displayName || subscription.user.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {subscription.user.email || subscription.user.username}
                          </Typography>
                          <br />
                          <Chip label={formatUserRole(subscription.user.role)} size="small" color="primary" variant="outlined" />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{subscription.user.organizationName || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{subscription.plan.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {subscription.plan.durationValue}{subscription.plan.durationUnit}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium" color="primary">{formatPrice(subscription.plan.price)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatPaymentMethod(subscription.payment.method)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{subscription.projectCreditsTotal || 0}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="primary">{subscription.projectCreditsUsed || 0}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{subscription.maxUsers || 0}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="primary">{subscription.currentUsers || 0}</Typography>
                      </TableCell>
                      <TableCell>{getStatusChip(subscription.status)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatPaymentDate(subscription.payment.paidAt)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(subscription.endsAt)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="查看详情">
                          <IconButton size="small" onClick={() => handleViewDetails(subscription)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {pagination.totalPages > 1 && (
            <Box display="flex" justifyContent="center" p={2}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                onChange={(_, page) => setPagination(prev => ({ ...prev, page }))}
                color="primary"
              />
            </Box>
          )}
        </Card>
      ) : (
        <Card>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>订单号</TableCell>
                  <TableCell>用户信息</TableCell>
                  <TableCell>公司</TableCell>
                  <TableCell>套餐</TableCell>
                  <TableCell>金额</TableCell>
                  <TableCell>支付方式</TableCell>
                  <TableCell>支付状态</TableCell>
                  <TableCell>创建时间</TableCell>
                  <TableCell>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">暂无支付记录</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map((payment) => (
                    <TableRow key={payment.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">{payment.order_id}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{payment.user_name || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{payment.organization_name || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{payment.plan_name || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium" color="primary">{formatPrice(payment.amount_cents)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatPaymentMethod(payment.payment_method)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={payment.payment_status} color={payment.payment_status === 'completed' ? 'success' : 'warning'} size="small" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(payment.created_at)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="查看详情">
                          <IconButton size="small" onClick={() => handleViewDetails(payment)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {pagination.totalPages > 1 && (
            <Box display="flex" justifyContent="center" p={2}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                onChange={(_, page) => setPagination(prev => ({ ...prev, page }))}
                color="primary"
              />
            </Box>
          )}
        </Card>
      )}

      <Dialog open={detailDialog.open} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle>{activeTab === 'subscriptions' ? '订阅记录详情' : '支付记录详情'}</DialogTitle>
        <DialogContent>
          {detailDialog.record && (
            <Grid container spacing={2}>
              {activeTab === 'subscriptions' ? (
                <>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>订单信息</Typography>
                    <Box sx={{ pl: 2 }}>
                      <Typography variant="body2"><strong>订单号：</strong>{(detailDialog.record as SubscriptionRecord).orderId}</Typography>
                      <Typography variant="body2"><strong>状态：</strong>{getStatusChip((detailDialog.record as SubscriptionRecord).status)}</Typography>
                      <Typography variant="body2"><strong>支付时间：</strong>{formatPaymentDate((detailDialog.record as SubscriptionRecord).payment.paidAt)}</Typography>
                      <Typography variant="body2"><strong>到期时间：</strong>{formatDate((detailDialog.record as SubscriptionRecord).endsAt)}</Typography>
                      <Typography variant="body2"><strong>自动续费：</strong>{(detailDialog.record as SubscriptionRecord).autoRenew ? '是' : '否'}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>用户信息</Typography>
                    <Box sx={{ pl: 2 }}>
                      <Typography variant="body2"><strong>用户名：</strong>{(detailDialog.record as SubscriptionRecord).user.username}</Typography>
                      <Typography variant="body2"><strong>显示名：</strong>{(detailDialog.record as SubscriptionRecord).user.displayName || '-'}</Typography>
                      <Typography variant="body2"><strong>邮箱：</strong>{(detailDialog.record as SubscriptionRecord).user.email || '-'}</Typography>
                      <Typography variant="body2"><strong>角色：</strong>{formatUserRole((detailDialog.record as SubscriptionRecord).user.role)}</Typography>
                      <Typography variant="body2"><strong>公司：</strong>{(detailDialog.record as SubscriptionRecord).user.organizationName || '-'}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>套餐信息</Typography>
                    <Box sx={{ pl: 2 }}>
                      <Typography variant="body2"><strong>套餐名称：</strong>{(detailDialog.record as SubscriptionRecord).plan.name}</Typography>
                      <Typography variant="body2"><strong>价格：</strong>{formatPrice((detailDialog.record as SubscriptionRecord).plan.price)}</Typography>
                      <Typography variant="body2"><strong>有效期：</strong>{(detailDialog.record as SubscriptionRecord).plan.durationValue}{(detailDialog.record as SubscriptionRecord).plan.durationUnit}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>使用统计</Typography>
                    <Box sx={{ pl: 2 }}>
                      <Typography variant="body2"><strong>项目个数：</strong>{(detailDialog.record as SubscriptionRecord).projectCreditsTotal || 0}</Typography>
                      <Typography variant="body2"><strong>已使用个数：</strong>{(detailDialog.record as SubscriptionRecord).projectCreditsUsed || 0}</Typography>
                      <Typography variant="body2"><strong>最大子账号数：</strong>{(detailDialog.record as SubscriptionRecord).maxUsers || 0}</Typography>
                      <Typography variant="body2"><strong>当前子账号数：</strong>{(detailDialog.record as SubscriptionRecord).currentUsers || 0}</Typography>
                    </Box>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>支付信息</Typography>
                    <Box sx={{ pl: 2 }}>
                      <Typography variant="body2"><strong>订单号：</strong>{(detailDialog.record as PaymentRecord).order_id}</Typography>
                      <Typography variant="body2"><strong>金额：</strong>{formatPrice((detailDialog.record as PaymentRecord).amount_cents)}</Typography>
                      <Typography variant="body2"><strong>支付方式：</strong>{formatPaymentMethod((detailDialog.record as PaymentRecord).payment_method)}</Typography>
                      <Typography variant="body2"><strong>支付状态：</strong><Chip label={(detailDialog.record as PaymentRecord).payment_status} color={(detailDialog.record as PaymentRecord).payment_status === 'completed' ? 'success' : 'warning'} size="small" /></Typography>
                      <Typography variant="body2"><strong>创建时间：</strong>{formatDate((detailDialog.record as PaymentRecord).created_at)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>关联信息</Typography>
                    <Box sx={{ pl: 2 }}>
                      <Typography variant="body2"><strong>用户：</strong>{(detailDialog.record as PaymentRecord).user_name || '-'}</Typography>
                      <Typography variant="body2"><strong>公司：</strong>{(detailDialog.record as PaymentRecord).organization_name || '-'}</Typography>
                      <Typography variant="body2"><strong>套餐：</strong>{(detailDialog.record as PaymentRecord).plan_name || '-'}</Typography>
                      <Typography variant="body2"><strong>项目额度：</strong>{(detailDialog.record as PaymentRecord).project_credits_total || 0}</Typography>
                      <Typography variant="body2"><strong>最大用户数：</strong>{(detailDialog.record as PaymentRecord).max_users || 0}</Typography>
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>关闭</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


